import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import useAnimationSettings from "../../hooks/useAnimationSettings";

const VIOLETA = "#697B68";
const ROSA = "#E09767";
const PROFILE_IMAGE_URL = require("../../media/perfil-actual.jpg");

const techs = [
  "React",
  "Next.js",
  "React Native",
  "Expo",
  "Node.js",
  "SQL",
  "Firebase",
  "n8n",
  "OpenAI",
  "Gemini",
  "Codex",
  "Claude Code",
];

export default function About() {
  const navigate = useNavigate();
  const {
    reduceMotion,
    compactMotion,
    allowHoverAnimations,
    allowAmbientAnimations,
    durations,
    offsets,
  } = useAnimationSettings();

  return (
    <section
      className="page-hero relative min-h-screen w-full overflow-hidden"
    >
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, #EADFD2 0%, #F8F2EA 52%, #E9DED0 100%)" }} />
      <BackgroundTexture />

      <div className="page-container relative grid grid-cols-1 items-center gap-12 lg:grid-cols-[1.2fr,0.8fr] lg:gap-16">
        <motion.div
          initial={reduceMotion ? false : { opacity: 0, y: offsets.revealY + 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={
            reduceMotion
              ? { duration: 0 }
              : { duration: durations.hero, ease: [0.22, 1, 0.36, 1] }
          }
          className="min-w-0 space-y-8 text-center lg:text-left"
        >
          <motion.span
            initial={reduceMotion ? false : { opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={
              reduceMotion
                ? { duration: 0 }
                : { delay: compactMotion ? 0.12 : 0.2, duration: durations.reveal }
            }
            className="mx-auto inline-flex w-fit items-center gap-2 rounded-full px-4 py-2 text-xs font-bold tracking-wider uppercase shadow-sm backdrop-blur-[2px] lg:mx-0 md:backdrop-blur-sm"
            style={{
              backgroundColor: `${ROSA}18`,
              color: ROSA,
              border: `1.5px solid ${ROSA}30`,
            }}
          >
            <span className="relative flex h-2 w-2">
              {allowAmbientAnimations && (
                <span
                  className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-75"
                  style={{ backgroundColor: ROSA }}
                />
              )}
              <span
                className="relative inline-flex h-2 w-2 rounded-full"
                style={{ backgroundColor: ROSA }}
              />
            </span>
            Sobre mí
          </motion.span>

          <motion.h1
            className="space-y-4"
            initial={reduceMotion ? false : { opacity: 0, y: offsets.revealY }}
            animate={{ opacity: 1, y: 0 }}
            transition={
              reduceMotion
                ? { duration: 0 }
                : { delay: compactMotion ? 0.18 : 0.3, duration: durations.hero }
            }
          >
            <span
              className="hero-name block tracking-tight"
              style={{ color: VIOLETA }}
            >
              Sebas Sotelo
            </span>
            <span
              className="hero-role block"
              style={{
                background: `linear-gradient(135deg, ${ROSA} 0%, ${VIOLETA} 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Full Stack Software Developer & AI Engineer
            </span>
            <span
              className="hero-subtitle hidden sm:block"
              style={{ color: `${VIOLETA}90` }}
            >
              Desarrollo web, mobile e inteligencia artificial
            </span>
          </motion.h1>

          <motion.div
            className="space-y-5"
            initial={reduceMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={
              reduceMotion
                ? { duration: 0 }
                : { delay: compactMotion ? 0.24 : 0.4, duration: durations.hero }
            }
          >
            <p
              className="body-copy mx-auto max-w-xl lg:mx-0"
              style={{ color: `${VIOLETA}E6` }}
            >
              Soy <strong style={{ color: VIOLETA }}>Full Stack Software Developer & AI Engineer</strong>.
              {" "}Diseño y desarrollo aplicaciones web y mobile, APIs y soluciones con inteligencia artificial. Trabajo con frontend, backend y bases de datos para construir software con foco en rendimiento, escalabilidad y necesidades del negocio.
            </p>
          </motion.div>

          <motion.ul
            className="hidden flex-wrap justify-center gap-3 sm:flex lg:justify-start"
            initial={reduceMotion ? false : { opacity: 0, y: offsets.subtleY }}
            animate={{ opacity: 1, y: 0 }}
            transition={
              reduceMotion
                ? { duration: 0 }
                : { delay: compactMotion ? 0.3 : 0.5, duration: durations.reveal }
            }
          >
            {techs.map((t, i) => (
              <motion.li
                key={t}
                initial={reduceMotion ? false : { opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={
                  reduceMotion
                    ? { duration: 0 }
                    : {
                        delay: (compactMotion ? 0.3 : 0.5) + i * durations.stagger,
                        duration: durations.reveal,
                      }
                }
                whileHover={
                  allowHoverAnimations ? { scale: 1.05, y: -2 } : undefined
                }
                className="rounded-xl px-4 py-2 text-sm font-semibold shadow-sm backdrop-blur-[2px] md:backdrop-blur-sm transition-all cursor-default"
                style={{
                  color: VIOLETA,
                  backgroundColor: "rgba(255,255,255,0.95)",
                  border: `1.5px solid ${VIOLETA}25`,
                }}
              >
                {t}
              </motion.li>
            ))}
          </motion.ul>

          <motion.div
            className="flex w-full flex-col items-stretch gap-3 pt-3 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:gap-4 sm:pt-4"
            initial={reduceMotion ? false : { opacity: 0, y: offsets.subtleY }}
            animate={{ opacity: 1, y: 0 }}
            transition={
              reduceMotion
                ? { duration: 0 }
                : { delay: compactMotion ? 0.36 : 0.6, duration: durations.reveal }
            }
          >
            <motion.a
              href="https://drive.google.com/file/d/1jgHbQDQlJLToev5Yn06qrox_4_TeR6n2/view?usp=sharing"
              target="_blank"
              rel="noreferrer"
              whileHover={allowHoverAnimations ? { scale: 1.03 } : undefined}
              whileTap={allowHoverAnimations ? { scale: 0.98 } : undefined}
              className="group motion-gpu relative inline-flex items-center justify-center gap-2 w-full rounded-2xl px-6 py-3.5 sm:w-auto sm:px-7 font-bold text-white shadow-md md:shadow-lg transition-all hover:shadow-lg md:hover:shadow-2xl"
              style={{ backgroundColor: VIOLETA }}
            >
              <span className="relative z-10">Ver CV</span>
              {allowAmbientAnimations && (
                <motion.span
                  className="relative z-10"
                  animate={{ x: [0, 3, 0] }}
                  transition={{
                    repeat: Infinity,
                    duration: 1.5,
                    ease: "easeInOut",
                  }}
                >
                  ->
                </motion.span>
              )}
              <span
                className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                style={{
                  background: `linear-gradient(135deg, ${ROSA}, ${VIOLETA})`,
                }}
              />
            </motion.a>

            <motion.a
              href="https://www.linkedin.com/in/sebassotelo/"
              target="_blank"
              rel="noreferrer"
              whileHover={allowHoverAnimations ? { scale: 1.03 } : undefined}
              whileTap={allowHoverAnimations ? { scale: 0.98 } : undefined}
              className="motion-gpu inline-flex items-center justify-center w-full rounded-2xl px-6 py-3.5 sm:w-auto sm:px-7 font-bold shadow-sm backdrop-blur-[2px] md:backdrop-blur-sm transition-all hover:shadow md:hover:shadow-md"
              style={{
                border: `2px solid ${VIOLETA}`,
                color: VIOLETA,
                backgroundColor: "rgba(255,255,255,0.95)",
              }}
            >
              LinkedIn
            </motion.a>

            <motion.button
              onClick={() => navigate("/sobre-mi#portfolio")}
              whileHover={allowHoverAnimations ? { scale: 1.03 } : undefined}
              whileTap={allowHoverAnimations ? { scale: 0.98 } : undefined}
              className="motion-gpu inline-flex items-center justify-center w-full rounded-2xl px-6 py-3.5 sm:w-auto sm:px-7 font-bold shadow-sm backdrop-blur-[2px] md:backdrop-blur-sm transition-all hover:shadow md:hover:shadow-md"
              style={{
                border: `2px solid ${ROSA}`,
                color: ROSA,
                backgroundColor: "rgba(255,255,255,0.95)",
              }}
            >
              Proyectos
            </motion.button>
          </motion.div>
        </motion.div>

        <motion.div
          initial={
            reduceMotion
              ? false
              : { opacity: 0, scale: 0.96, rotateY: compactMotion ? -6 : -10 }
          }
          animate={{ opacity: 1, scale: 1, rotateY: 0 }}
          transition={
            reduceMotion
              ? { duration: 0 }
              : {
                  duration: durations.hero,
                  delay: compactMotion ? 0.14 : 0.2,
                  ease: [0.22, 1, 0.36, 1],
                }
          }
          className="min-w-0"
        >
          <div className="relative mx-auto w-full max-w-[18rem] sm:max-w-[24rem]">
            {allowAmbientAnimations && (
              <>
                <motion.div
                  animate={{ scale: [1, 1.05, 1], opacity: [0.28, 0.42, 0.28] }}
                  transition={{
                    repeat: Infinity,
                    duration: 4,
                    ease: "easeInOut",
                  }}
                  className="absolute -inset-4 -z-10 rounded-[3rem] blur-xl md:blur-3xl"
                  style={{
                    background: `linear-gradient(135deg, ${ROSA} 0%, ${VIOLETA} 100%)`,
                  }}
                />
                <motion.div
                  animate={{ scale: [1, 1.08, 1], opacity: [0.15, 0.28, 0.15] }}
                  transition={{
                    repeat: Infinity,
                    duration: 5,
                    ease: "easeInOut",
                    delay: 0.5,
                  }}
                  className="absolute -inset-8 -z-20 rounded-[3rem] blur-xl md:blur-3xl"
                  style={{
                    background: `linear-gradient(135deg, ${VIOLETA} 0%, ${ROSA} 100%)`,
                  }}
                />
              </>
            )}

            <motion.div
              whileHover={allowHoverAnimations ? { y: -6 } : undefined}
              transition={{ duration: durations.hover }}
              className="motion-gpu relative overflow-hidden rounded-[1.75rem] p-4 shadow-lg backdrop-blur-[2px] sm:p-6 md:rounded-[2.5rem] md:p-7 md:shadow-2xl md:backdrop-blur-sm"
              style={{
                backgroundColor: "rgba(255,255,255,0.98)",
                border: `2px solid ${VIOLETA}15`,
              }}
            >
              <div className="relative mx-auto aspect-square max-w-sm overflow-hidden rounded-[2rem]">
                <img
                  src={PROFILE_IMAGE_URL}
                  sizes="(max-width: 640px) 288px, 384px"
                  width={1000}
                  height={1000}
                  alt="Sebas Sotelo"
                  fetchPriority="high"
                  decoding="async"
                  loading="eager"
                  className={`h-full w-full object-contain transition-transform duration-500 ${
                    allowHoverAnimations ? "hover:scale-105" : ""
                  }`}
                />
                <div
                  className={`absolute inset-0 opacity-0 transition-opacity duration-300 ${
                    allowHoverAnimations ? "hover:opacity-10" : ""
                  }`}
                  style={{
                    background: `linear-gradient(135deg, ${ROSA}, ${VIOLETA})`,
                  }}
                />
              </div>

              <div className="mt-4 hidden grid-cols-3 gap-3 sm:grid sm:mt-6">
                {[
                  { label: "Full Stack", icon: "FS" },
                  { label: "AI Engineering", icon: "AI" },
                  { label: "Mobile", icon: "RN" },
                ].map((item, i) => (
                  <motion.div
                    key={item.label}
                    initial={reduceMotion ? false : { opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={
                      reduceMotion
                        ? { duration: 0 }
                        : {
                            delay: (compactMotion ? 0.4 : 0.7) + i * durations.stagger,
                            duration: durations.reveal,
                          }
                    }
                    whileHover={
                      allowHoverAnimations ? { scale: 1.05, y: -2 } : undefined
                    }
                    className="motion-gpu grid place-items-center gap-1 rounded-2xl px-3 py-3 text-center shadow-sm backdrop-blur-[2px] md:backdrop-blur-sm transition-all"
                    style={{
                      backgroundColor: `${ROSA}12`,
                      border: `1.5px solid ${VIOLETA}18`,
                    }}
                  >
                    <span className="text-lg font-black" style={{ color: ROSA }}>
                      {item.icon}
                    </span>
                    <span
                      className="text-xs font-bold leading-tight"
                      style={{ color: VIOLETA }}
                    >
                      {item.label}
                    </span>
                  </motion.div>
                ))}
              </div>

              <div
                className="absolute top-7 right-7 h-16 w-16 rounded-full opacity-20 blur-xl md:blur-2xl"
                style={{
                  background: `linear-gradient(135deg, rgba(224,151,103,0.18), transparent)`,
                }}
              />
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function BackgroundTexture() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "linear-gradient(90deg, #697B68 1px, transparent 1px), linear-gradient(#697B68 1px, transparent 1px)",
          backgroundSize: "52px 52px",
        }}
      />
      <div
        className="absolute inset-x-0 bottom-0 h-32"
        style={{ background: "linear-gradient(0deg, rgba(224,151,103,0.16), transparent)" }}
      />
    </div>
  );
}
